import { Box } from "@mui-wrapper";
import { useSearchParams } from "react-router-dom";
import { QueryParams } from "@router/route-params";
import { isValidServerIdentifier } from "@utils/ulm-utils";
import { useEffect, useState } from "react";
import {
  REGION_SERVER_IDENTIFIER,
  SERVER_IDENTIFIER_ICON,
  SERVER_IDENTIFIER_LABEL,
} from "@constants/server-constants";
import { FaroCoreApiConst, FaroCoreApiTypes } from "@stellar/core-api-client";
import { runtimeConfig } from "@src/runtime-config";
import { useRedirectToOtherServer } from "@hooks/use-redirect-to-other-server";
import { Tooltip, QuestionMarkInCircleIcon } from "@flat-ui";
import { UlmInputLabel } from "@components/ulm/ulm-input-label";
import { TrialServerButton } from "@pages/trial/trial-server-button";

/** Server identifier for the current region */
const regionServerIdentifier =
  REGION_SERVER_IDENTIFIER[runtimeConfig.appRegion];

interface Props {
  /** Callback to set whether the user is in the correct server */
  setIsInCorrectServer: (isInCorrectServer: boolean) => void;
}

/** Renders the buttons for the users to select a server region */
export function TrialServer({ setIsInCorrectServer }: Props): JSX.Element {
  const redirectToOtherServer = useRedirectToOtherServer();
  const [searchParams] = useSearchParams();

  const serverIdentifierParam = searchParams.get(QueryParams.server);
  const preselectedServerIdentifier =
    serverIdentifierParam && isValidServerIdentifier(serverIdentifierParam)
      ? serverIdentifierParam
      : null;
  const initialServerIdentifier =
    preselectedServerIdentifier ?? regionServerIdentifier;
  const [serverIdentifier, setServerIdentifier] =
    useState<FaroCoreApiTypes.ServerIdentifier>(initialServerIdentifier);

  // Redirect to the other server if the selected server identifier
  //  is different from the current region server identifier
  useEffect(() => {
    if (serverIdentifier !== regionServerIdentifier) {
      setIsInCorrectServer(false);
      redirectToOtherServer();
    } else {
      setIsInCorrectServer(true);
    }
  }, [redirectToOtherServer, serverIdentifier, setIsInCorrectServer]);

  return preselectedServerIdentifier !== null ? (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        fontSize: "14px",
        width: "100%",
        border: "1px solid",
        borderColor: "input.enabledBorder",
        borderRadius: "4px",
        height: "40px",
        paddingLeft: "10px",
        marginBottom: "24px",
      }}
    >
      {SERVER_IDENTIFIER_ICON[preselectedServerIdentifier]}
      {SERVER_IDENTIFIER_LABEL[preselectedServerIdentifier]}
    </Box>
  ) : (
    <>
      <UlmInputLabel
        title="Server"
        helpComponent={
          <Tooltip title="The Sphere XG Cloud is hosted on servers within either Europe or North America. Choose the server depending on your location and IP address.">
            <QuestionMarkInCircleIcon fontSize="extraExtraSmall" />
          </Tooltip>
        }
      />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
        }}
      >
        {FaroCoreApiConst.allServerIdentifiers.map((server) => (
          <TrialServerButton
            key={server}
            serverIdentifier={server}
            onClick={setServerIdentifier}
            selectedServerIdentifier={serverIdentifier}
          />
        ))}
      </Box>
    </>
  );
}
